<template>
  <div class="job-card mb-2" @click="$emit('click', job)">
    <!-- Desktop layout: 2 rows -->
    <div class="desktop-layout">
      <!-- First row: Company name (left) and Scout date (right) -->
      <div class="row-top d-flex justify-content-between align-items-center">
        <div class="company-name">{{ companyName || job.companyName }}</div>
        <div class="scout-date" v-if="isJobScouted && formattedScoutDate">スカウト希望日 {{ formattedScoutDate }}</div>
      </div>
      
      <!-- Second row: Job title (left) and Scout badge (right) -->
      <div class="row-bottom d-flex justify-content-between align-items-center">
        <div class="job-title-container">
          <div class="job-title" v-html="highlightText(job.job_title, 'job_title')"></div>
        </div>
        <div v-if="isJobScouted || (!isJobScouted && isProfileSharedCompany)" class="scouted-badge">
          <div v-if="isJobScouted" :class="['scouted-badge-text', isExpired ? 'expired' : 'active']">
            {{ isExpired ? 'スカウト希望期間終了' : 'スカウト希望中' }}
          </div>
          <div v-else-if="!isJobScouted && isProfileSharedCompany" class="scouted-badge-text profile-shared">
            あなたがキャリア登録している企業
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile layout: 3 rows -->
    <div class="mobile-layout">
      <!-- Line 1: Company name -->
      <div class="mobile-row-1">
        <div class="company-name">{{ companyName || job.companyName }}</div>
      </div>
      
      <!-- Line 2: Job title -->
      <div class="mobile-row-2">
        <div class="job-title-container">
          <div class="job-title" v-html="highlightText(job.job_title, 'job_title')"></div>
        </div>
      </div>
      
      <!-- Line 3: Scout date (left) and Scout badge (right) -->
      <div class="mobile-row-3 mt-2 d-flex justify-content-between align-items-center">
        <div class="scout-date" v-if="isJobScouted && formattedScoutDate">スカウト希望日 {{ formattedScoutDate }}</div>
        <div v-if="isJobScouted || (!isJobScouted && isProfileSharedCompany)" class="scouted-badge">
          <div v-if="isJobScouted" :class="['scouted-badge-text', isExpired ? 'expired' : 'active']">
            {{ isExpired ? 'スカウト希望期間終了' : 'スカウト希望中' }}
          </div>
          <div v-else-if="!isJobScouted && isProfileSharedCompany" class="scouted-badge-text profile-shared">
            あなたがキャリア登録している企業
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

export default {
  name: "JobListItem",
  props: {
    job: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isJobScouted: {
      type: Boolean,
      required: true,
    },
    companyName: {
      type: String,
      required: false,
    },
    searchHighlights: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
    isProfileSharedCompany() {
      const companyId = this.job.company_id || this.job.companyId;
      return this.profile && this.profile.other_companies && companyId && this.profile.other_companies.includes(companyId);
    },
    isExpired() {
      return this.job.status === 'expired';
    },
    formattedScoutDate() {
      if (!this.job.scoutedAt) return '';
      return moment(this.job.scoutedAt).format('MM/DD');
    }
  },
  methods: {
    highlightText(text, field) {
      if (!text || !this.searchHighlights.length) return text;
      
      const fieldHighlights = this.searchHighlights.find(h => h.path === field);
      if (!fieldHighlights || !fieldHighlights.texts) return text;

      // Get hits and sort by length (longest first)
      const hits = fieldHighlights.texts
        .filter(text => text.type === 'hit')
        .map(text => text.value)
        .sort((a, b) => b.length - a.length);

      if (!hits.length) return text;

      // First handle full-width spaces between hits
      let highlightedText = this.highlightFullWidthSpaces(text, hits);
      // Then highlight the hits
      // console.log(highlightedText);
      return this.highlightHits(highlightedText, hits);
    },

    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(hit2)})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight">${match}</span>`;
          }
          return match;
        });
      }, text);
    },

    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }
};
</script>

<style scoped>
.job-card {
  background-color: white;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 0px 4px 4px 0px #00000040;
  padding: 5px 53px;
}

.job-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Desktop Layout (default) */
.desktop-layout {
  display: flex;
  flex-direction: column;
}

.mobile-layout {
  display: none;
}

.row-top {
  margin-bottom: 2px;
}

.row-bottom {
  margin-top: 0;
}

.company-name {
  font-weight: 600;
  font-size: 16px;
  color: #3c3c3c;
  flex: 1;
  min-width: 0;
}

.scout-date {
  font-size: 12px;
  color: #3c3c3c;
  white-space: nowrap;
}

.job-title-container {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.job-title {
  font-size: 14px;
  font-weight: 400;
  color: #3c3c3c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  display: block;
}

/* Mobile Layout */
@media (max-width: 767px) {
  .job-card {
    /* width: 340px; */
    height: 100px;
    padding: 15px 16px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-shadow: 0px 4px 4px 0px #00000040;
  }

  .desktop-layout {
    display: none;
  }

  .mobile-layout {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
  }

  .mobile-row-1,
  .mobile-row-2,
  .mobile-row-3 {
    display: flex;
    align-items: center;
  }

  .mobile-row-1 {
    flex-shrink: 0;
  }

  .mobile-row-2 {
    flex: 1;
    min-height: 0;
  }

  .mobile-row-2 .job-title-container {
    width: 100%;
  }

  .mobile-row-3 {
    flex-shrink: 0;
  }

  .mobile-layout .company-name {
    font-size: 16px;
    font-weight: 600;
    color: #3c3c3c;
  }

  .mobile-layout .job-title {
    font-size: 14px;
    font-weight: 400;
    color: #3c3c3c;
  }

  .mobile-layout .scout-date {
    font-size: 12px;
    color: #3c3c3c;
  }
}

/* Desktop Layout */
@media (min-width: 768px) {
  .job-card {
    border-radius: 10px;
    padding: 5px 52px;
    box-shadow: 0px 4px 4px 0px #00000040;
  }
  
  .desktop-layout {
    display: flex;
    flex-direction: column;
  }

  .mobile-layout {
    display: none;
  }

  .row-top {
    margin-bottom: 2px;
  }

  .scout-date {
    margin-right: 20px;
  }

  .job-title-container {
    margin-right: 10px;
  }
}

.scouted-badge {
  flex-shrink: 0;
  margin-left: auto;
}

.scouted-badge-text {
  min-width: 168px;
  height: 25px;
  font-weight: bold;
  text-align: center;
  font-size: 10px;
  padding: 5px 10px;
  border-radius: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scouted-badge-text.active {
  background-color: #14B8A6;
  color: #ffffff;
  border: 1px solid #14B8A6;
  width: 168px;
}

.scouted-badge-text.expired {
  background-color: #C4C4C4;
  color: #515C6B;
  border: 1px solid #C4C4C4;
  width: auto;
}

.scouted-badge-text.profile-shared {
  background-color: #C4C4C4;
  color: #515C6B;
  border: 1px solid #C4C4C4;
  width: 230px;
  font-size: 12px;
}

.job-description {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  word-wrap: break-word;
  white-space: normal;
}

::v-deep .highlight {
  background-color: #ffd700;
  padding: 0 2px;
  border-radius: 2px;
}

@media (min-width: 1024px) {
  .job-title-container {
    max-width: 600px;
  }
}
</style>
